import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, ClipboardList, AlertTriangle, Plus } from 'lucide-react';
import { apiClient } from '@/services/api';
import { CreateProjectForm } from '@/components/CreateProjectForm';
import { StatusBadge } from '@shared/components/StatusBadge';
import { Modal } from '@shared/components/Modal';
import { Skeleton } from '@connor-music/shared';
import type { Project, DashboardMetrics } from '@shared/types';
import toast from 'react-hot-toast';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadData = async () => {
    try {
      const [projectData, metricsData] = await Promise.all([
        apiClient.getProjects(),
        apiClient.getProjectMetrics(),
      ]);
      setProjects(Array.isArray(projectData) ? projectData : []);
      setMetrics(metricsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (data: { name: string; description: string; platform_id?: string; due_date?: string }) => {
    try {
      await apiClient.createProject({
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        platform_id: data.platform_id || undefined,
        due_date: data.due_date || undefined,
      });
      toast.success('Project created successfully');
      setShowCreateModal(false);
      await loadData();
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  const stats = [
    { label: 'Awaiting Upload', value: metrics?.awaiting_upload ?? 0, icon: FolderKanban, color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400' },
    { label: 'In Review', value: metrics?.in_review ?? 0, icon: ClipboardList, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400' },
    { label: 'Overdue', value: metrics?.overdue ?? 0, icon: AlertTriangle, color: 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400' },
  ];

  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  const needsAttention = metrics?.needs_attention ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Overview of audit projects and activity.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-accent-600 hover:bg-accent-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </button>
      </div>

      {/* Metrics cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-gray-800 overflow-hidden rounded-lg shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-3 ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      {stat.label}
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {isLoading ? <Skeleton className="h-7 w-12" /> : stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Needs Attention */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Needs Attention</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              </div>
            ))
          ) : needsAttention.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              All caught up — no projects need attention.
            </div>
          ) : (
            needsAttention.map((project) => {
              const isOverdue = project.due_date && project.due_date < new Date().toISOString().split('T')[0];
              return (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {project.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {project.attention_reason}
                        </span>
                        {project.due_date && (
                          <span className={`text-xs ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                            Due {formatDate(project.due_date)}
                            {isOverdue && ' (overdue)'}
                          </span>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={project.status} type="project" />
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-700 dark:text-gray-300">Recent Projects</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              </div>
            ))
          ) : recentProjects.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              No projects yet. Create your first audit project to get started.
            </div>
          ) : (
            recentProjects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {project.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {project.customer_count} customers &middot; {project.upload_count} uploads
                    </p>
                  </div>
                  <StatusBadge status={project.status} type="project" />
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* Create project modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
      >
        <CreateProjectForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>
    </div>
  );
}
